import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUploadCloud } from 'react-icons/fi';

export default function WhrList() {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState(false);

  const [commodities, setCommodities] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [commoditySearch, setCommoditySearch] = useState('');
  const [commodityDropdownOpen, setCommodityDropdownOpen] = useState(false);

  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState('');
  const [schemeSearch, setSchemeSearch] = useState('');
  const [schemeDropdownOpen, setSchemeDropdownOpen] = useState(false);

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [seasonSearch, setSeasonSearch] = useState('');
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);

  const [showWHRModal, setShowWHRModal] = useState(false);
  const [whrLoading, setWhrLoading] = useState(false);
  const [whrError, setWhrError] = useState('');
  const [whrSuccess, setWhrSuccess] = useState('');
  const [whrForm, setWhrForm] = useState({
    commodity: '',
    warehouse: '',
    season: '',
    scheme: '',
    file: null,
  });

  const [whrWarehouseSearch, setWhrWarehouseSearch] = useState('');
  const [whrWarehouseDropdownOpen, setWhrWarehouseDropdownOpen] = useState(false);

  const [role, setRole] = useState('');
  const [userBranchId, setUserBranchId] = useState('');

  const token = localStorage.getItem('token');

  const fetchData = async (
    pageNo = 1,
    limitNum = 10,
    warehouseId = '',
    commodityId = '',
    schemeId = '',
    seasonId = ''
  ) => {
    setLoading(true);
    try {
      let url = `inventory/whr/list?page=${pageNo}&limit=${limitNum}`;
      if (warehouseId) url += `&warehouse_id=${warehouseId}`;
      if (commodityId) url += `&commodity_id=${commodityId}`;
      if (schemeId) url += `&scheme_id=${schemeId}`;
      if (seasonId) url += `&season_id=${seasonId}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(res.data.data || []);
      setTotal(res.data.total || 0);
      setError('');
    } catch (err) {
      console.error('Error fetching WHR list:', err.response?.data || err.message);
      setError('Failed to fetch WHR list.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await axios.get('warehouse?paginate=false', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWarehouses(res.data || []);
    } catch (err) {
      console.error('Error fetching warehouses:', err.message);
    }
  };

  const fetchCommodities = async () => {
    try {
      const res = await axios.get('commodity', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCommodities(res.data || []);
    } catch (err) {
      console.error('Error fetching commodities:', err.message);
    }
  };

  const fetchSchemes = async () => {
    try {
      const res = await axios.get('schemes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchemes(res.data || []);
    } catch (err) {
      console.error('Error fetching schemes:', err.message);
    }
  };

  const fetchSeasons = async () => {
    try {
      const res = await axios.get('seasons?paginate=false', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSeasons(res.data || []);
    } catch (err) {
      console.error('Error fetching seasons:', err.message);
    }
  };

  const filteredWarehouses = warehouses.filter((w) =>
    (w?.warehouse_name || '')
      .toLowerCase()
      .includes(warehouseSearch.toLowerCase())
  );

  const filteredCommodities = commodities.filter((c) =>
    (c?.commodityname || '')
      .toLowerCase()
      .includes(commoditySearch.toLowerCase())
  );

  const filteredSchemes = schemes.filter((s) =>
    (s?.name || '').toLowerCase().includes(schemeSearch.toLowerCase())
  );

  const filteredSeasons = seasons.filter((s) =>
    (s?.name || '').toLowerCase().includes(seasonSearch.toLowerCase())
  );

  const clearFilters = () => {
    closeAllDropdowns();
    setSelectedWarehouse('');
    setWarehouseSearch('');
    setSelectedCommodity('');
    setCommoditySearch('');
    setSelectedScheme('');
    setSchemeSearch('');
    setSelectedSeason('');
    setSeasonSearch('');
    setPage(1);
    fetchData(1, limit, '', '', '', '');
  };

  const closeAllDropdowns = () => {
    setWarehouseDropdownOpen(false);
    setCommodityDropdownOpen(false);
    setSchemeDropdownOpen(false);
    setSeasonDropdownOpen(false);
  };

  const closeWhrModalDropdowns = () => {
    setWhrWarehouseDropdownOpen(false);
  };

  const filteredWareHousesForWHR = warehouses.filter((w) => {
    const matchesSearch = (w?.warehouse_name || '')
      .toLowerCase()
      .includes(whrWarehouseSearch.toLowerCase());
    const matchesBranch =
      role === 'Branch' && userBranchId
        ? w?.branch?.id === userBranchId
        : true;
    return matchesSearch && matchesBranch;
  });

  const resetWhrModalForm = () => {
    setWhrForm({
      commodity: '',
      warehouse: '',
      season: '',
      scheme: '',
      file: null,
    });
    setWhrWarehouseSearch('');
    closeWhrModalDropdowns();
    setWhrError('');
    setWhrSuccess('');
  };

  const handleWhrFormChange = (field, value) => {
    setWhrForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWhrFileChange = (e) => {
    setWhrForm((prev) => ({
      ...prev,
      file: e.target.files[0],
    }));
  };

  const handleWhrSubmit = async () => {
    setWhrError('');
    setWhrSuccess('');

    if (!whrForm.warehouse) {
      setWhrError('Please select a warehouse');
      return;
    }
    if (!whrForm.commodity) {
      setWhrError('Please select a commodity');
      return;
    }
    if (!whrForm.scheme) {
      setWhrError('Please select a scheme');
      return;
    }
    if (!whrForm.season) {
      setWhrError('Please select a season');
      return;
    }
    if (!whrForm.file) {
      setWhrError('Please select a file to upload');
      return;
    }

    const selectedWarehouseObj = warehouses.find((w) => w.id === whrForm.warehouse);
    if (!selectedWarehouseObj) {
      setWhrError('Invalid warehouse selected');
      return;
    }

    const warehouseCode = selectedWarehouseObj.warehouse_code;
    if (!warehouseCode) {
      setWhrError('Warehouse code not available');
      return;
    }

    const formData = new FormData();
    formData.append('warehouse_code', warehouseCode);
    formData.append('commodity', whrForm.commodity);
    formData.append('scheme', whrForm.scheme);
    formData.append('season', whrForm.season);
    formData.append('file', whrForm.file);

    try {
      setWhrLoading(true);
      await axios.post('inventory/whr/store', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setWhrSuccess('✅ WHR uploaded successfully!');

      setTimeout(() => {
        resetWhrModalForm();
        setShowWHRModal(false);
        setPage(1);
        fetchData(
          1,
          limit,
          selectedWarehouse,
          selectedCommodity,
          selectedScheme,
          selectedSeason
        );
      }, 1500);
    } catch (err) {
      console.error('WHR Upload Error:', err.response?.data || err.message);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to upload WHR. Please try again.';
      setWhrError(`⚠️ ${errorMessage}`);
    } finally {
      setWhrLoading(false);
    }
  };

  const initialDataFetchedRef = useRef(false);
  const fetchDataRef = useRef(null);

  useEffect(() => {
    if (!initialDataFetchedRef.current) {
      initialDataFetchedRef.current = true;
      fetchWarehouses();
      fetchCommodities();
      fetchSchemes();
      fetchSeasons();

      const user = JSON.parse(localStorage.getItem('user'));
      setRole(user?.roles?.[0]?.name || '');
      setUserBranchId(user?.branch?.id || '');
    }
  }, []);

  useEffect(() => {
    if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    fetchDataRef.current = setTimeout(() => {
      fetchData(
        page,
        limit,
        selectedWarehouse,
        selectedCommodity,
        selectedScheme,
        selectedSeason
      );
    }, 100);
    return () => {
      if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    };
  }, [page, limit, selectedWarehouse, selectedCommodity, selectedScheme, selectedSeason]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const formatUserName = (user) => {
    if (!user) return '—';
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length ? parts.join(' ') : '—';
  };

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderDropdown = ({
    label,
    selected,
    search,
    setSearch,
    open,
    setOpen,
    onClear,
    filteredItems,
    onSelect,
    getLabel,
    placeholder,
    onToggle,
    zIndex = 'z-10',
    resetPageOnSelect = true,
    className = 'relative flex-1 min-w-[200px]',
  }) => (
    <div className={className}>
      <div
        onClick={() => {
          if (onToggle) onToggle();
          else {
            closeAllDropdowns();
            setOpen((prev) => !prev);
          }
        }}
        className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center"
      >
        <span>{selected ? search || `${label} Selected` : `Select ${label}`}</span>
        <div className="flex items-center gap-2">
          {selected && (
            <FiX
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
              title="Clear"
            />
          )}
          <span className="text-gray-500">{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && (
        <div
          className={`absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 ${zIndex}`}
        >
          <input
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border-b text-sm focus:outline-none"
          />
          {filteredItems.length === 0 && (
            <p className="p-2 text-gray-500">No {label.toLowerCase()} found</p>
          )}
          {filteredItems.map((item) => (
            <div
              key={item.id}
              onClick={() => {
                onSelect(item);
                setOpen(false);
                if (resetPageOnSelect) setPage(1);
              }}
              className={`p-2 cursor-pointer hover:bg-blue-100 ${
                selected === item.id ? 'bg-blue-200 font-semibold' : ''
              }`}
            >
              {getLabel(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10 text-gray-900">
      <div className="flex flex-wrap items-center justify-between mb-8">
        <motion.h1
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-extrabold text-blue-800 drop-shadow-sm"
        >
          📄 WHR List
        </motion.h1>
        {role !== 'User' && (
          <button
            onClick={() => {
              resetWhrModalForm();
              setShowWHRModal(true);
            }}
            className="bg-purple-600 text-white px-6 py-2 rounded-xl shadow-md hover:bg-purple-700 flex items-center gap-2"
          >
            <FiUploadCloud /> Upload WHR
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        {renderDropdown({
          label: 'Season',
          selected: selectedSeason,
          search: seasonSearch,
          setSearch: setSeasonSearch,
          open: seasonDropdownOpen,
          setOpen: setSeasonDropdownOpen,
          onClear: () => {
            setSelectedSeason('');
            setSeasonSearch('');
            setPage(1);
          },
          filteredItems: filteredSeasons,
          onSelect: (c) => {
            setSelectedSeason(c.id);
            setSeasonSearch(c.name);
          },
          getLabel: (c) => c.name,
          placeholder: 'Search Season...',
        })}
        {renderDropdown({
          label: 'Scheme',
          selected: selectedScheme,
          search: schemeSearch,
          setSearch: setSchemeSearch,
          open: schemeDropdownOpen,
          setOpen: setSchemeDropdownOpen,
          onClear: () => {
            setSelectedScheme('');
            setSchemeSearch('');
            setPage(1);
          },
          filteredItems: filteredSchemes,
          onSelect: (s) => {
            setSelectedScheme(s.id);
            setSchemeSearch(s.name);
          },
          getLabel: (s) => s.name,
          placeholder: 'Search Scheme...',
        })}
        {renderDropdown({
          label: 'Commodity',
          selected: selectedCommodity,
          search: commoditySearch,
          setSearch: setCommoditySearch,
          open: commodityDropdownOpen,
          setOpen: setCommodityDropdownOpen,
          onClear: () => {
            setSelectedCommodity('');
            setCommoditySearch('');
            setPage(1);
          },
          filteredItems: filteredCommodities,
          onSelect: (c) => {
            setSelectedCommodity(c.id);
            setCommoditySearch(c.commodityname);
          },
          getLabel: (c) => c.commodityname,
          placeholder: 'Search Commodity...',
        })}
        {renderDropdown({
          label: 'Warehouse',
          selected: selectedWarehouse,
          search: warehouseSearch,
          setSearch: setWarehouseSearch,
          open: warehouseDropdownOpen,
          setOpen: setWarehouseDropdownOpen,
          onClear: () => {
            setSelectedWarehouse('');
            setWarehouseSearch('');
            setPage(1);
          },
          filteredItems: filteredWarehouses,
          onSelect: (w) => {
            setSelectedWarehouse(w.id);
            setWarehouseSearch(w.warehouse_name);
          },
          getLabel: (w) => w.warehouse_name,
          placeholder: 'Search Warehouse...',
        })}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={clearFilters}
          className="bg-red-500 text-white px-5 py-2 rounded-xl shadow-md hover:bg-red-600"
        >
          Clear Filters
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-6">
          {error}
        </div>
      )}

      <motion.div className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                {[
                  'State',
                  'District',
                  'Warehouse',
                  'Commodity',
                  'Scheme',
                  'Season',
                  'Uploaded By',
                  'WHR File',
                  'Created At',
                ].map((header) => (
                  <th
                    key={header}
                    className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-10 text-gray-500 font-semibold">
                    Loading...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((item) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="py-4 px-6 text-sm text-gray-700 font-medium">
                      {item.warehouse?.state?.name || '—'}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.warehouse?.district?.name || '—'}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">
                          {item.warehouse?.warehouse_name || '—'}
                        </span>
                        <span className="text-xs text-blue-500 mt-1">
                          {item.warehouse?.warehouse_code || ''}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm text-blue-700 font-medium">
                      {item.commodity?.commodityname || '—'}
                    </td>
                    <td className="py-4 px-6 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {item.scheme?.name || '—'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.season?.name || '—'}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {formatUserName(item.user)}
                    </td>
                    <td className="py-4 px-6 text-sm">
                      {item.whr_file ? (
                        <a
                          href={item.whr_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        >
                          View / Download
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">No file</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {formatDate(item.createdAt)}
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-12 text-gray-500 font-medium">
                    No WHR records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      <div className="flex flex-wrap justify-between items-center mt-8 gap-4">
        <select
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(1);
          }}
          className="px-4 py-2 border rounded-xl bg-white shadow-sm text-base min-w-[120px]"
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              Limit: {n}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-4">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className={`px-6 py-3 rounded-xl text-lg font-medium shadow-md transition-all ${
              page === 1
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Prev
          </button>
          <span className="text-xl font-semibold text-gray-700">
            Page {page} of {totalPages} ({total} records)
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className={`px-6 py-3 rounded-xl text-lg font-medium shadow-md transition-all ${
              page >= totalPages
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showWHRModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl p-8 w-[90%] max-w-[600px] max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <h2 className="text-2xl font-bold mb-6 text-blue-700">
                📤 Upload WHR
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="col-span-1 relative">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Warehouse
                  </label>
                  {renderDropdown({
                    label: 'Warehouse',
                    selected: whrForm.warehouse,
                    search: whrWarehouseSearch,
                    setSearch: setWhrWarehouseSearch,
                    open: whrWarehouseDropdownOpen,
                    setOpen: setWhrWarehouseDropdownOpen,
                    onClear: () => {
                      handleWhrFormChange('warehouse', '');
                      setWhrWarehouseSearch('');
                    },
                    filteredItems: filteredWareHousesForWHR,
                    onSelect: (w) => {
                      handleWhrFormChange('warehouse', w.id);
                      setWhrWarehouseSearch(w.warehouse_name);
                    },
                    getLabel: (w) => w.warehouse_name,
                    placeholder: 'Search Warehouse...',
                    onToggle: () => {
                      closeWhrModalDropdowns();
                      setWhrWarehouseDropdownOpen((prev) => !prev);
                    },
                    zIndex: 'z-[60]',
                    resetPageOnSelect: false,
                    className: 'relative w-full',
                  })}
                </div>

                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Commodity
                  </label>
                  <select
                    value={whrForm.commodity}
                    onChange={(e) => handleWhrFormChange('commodity', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Select Commodity</option>
                    {commodities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.commodityname}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Scheme
                  </label>
                  <select
                    value={whrForm.scheme}
                    onChange={(e) => handleWhrFormChange('scheme', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Select Scheme</option>
                    {schemes.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Season
                  </label>
                  <select
                    value={whrForm.season}
                    onChange={(e) => handleWhrFormChange('season', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Select Season</option>
                    {seasons.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Upload File
                  </label>
                  <div className="relative">
                    <input
                      id="whrFileInput"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                      onChange={handleWhrFileChange}
                    />
                    <div
                      onClick={() => document.getElementById('whrFileInput').click()}
                      className="w-full border rounded-lg px-3 py-2 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-sm flex justify-between items-center h-[42px] overflow-hidden"
                    >
                      <span className="truncate text-gray-500 max-w-[80%]">
                        {whrForm.file ? whrForm.file.name : 'Select file'}
                      </span>
                      {whrForm.file ? (
                        <span
                          className="text-red-500 hover:text-red-700 font-bold text-xs cursor-pointer ml-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWhrForm({ ...whrForm, file: null });
                            document.getElementById('whrFileInput').value = '';
                          }}
                        >
                          ✕
                        </span>
                      ) : (
                        <span className="text-gray-400">📎</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {whrError && (
                <motion.div
                  className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {whrError}
                </motion.div>
              )}

              {whrSuccess && (
                <motion.div
                  className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {whrSuccess}
                </motion.div>
              )}

              <div className="flex justify-between gap-3">
                <button
                  onClick={handleWhrSubmit}
                  disabled={whrLoading}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors"
                >
                  {whrLoading ? '⏳ Uploading...' : '✅ Upload'}
                </button>
                <button
                  onClick={() => {
                    setShowWHRModal(false);
                    resetWhrModalForm();
                  }}
                  disabled={whrLoading}
                  className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  ❌ Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
